import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Tags } from "lucide-react";

export default function CategoriesPage() {
  const { isAdmin } = useAuth();
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", default_unit: "adet" });

  const load = () => api.get("/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/categories", form);
      toast.success("Kategori eklendi");
      setOpen(false);
      setForm({ name: "", default_unit: "adet" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hata");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`"${c.name}" silinsin mi?`)) return;
    try {
      await api.delete(`/categories/${c.id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  return (
    <AppLayout
      title="İş Kategorileri"
      subtitle="Çalışma kayıtlarında kullanılan iş kategorileri"
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-category-button" className="bg-emerald-700 hover:bg-emerald-800">
              <Plus className="h-4 w-4 mr-1.5" /> Yeni Kategori
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="category-dialog">
            <DialogHeader>
              <DialogTitle>Yeni Kategori Ekle</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Kategori Adı *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       data-testid="category-name-input" />
              </div>
              <div>
                <Label>Varsayılan Birim</Label>
                <Input value={form.default_unit}
                       onChange={(e) => setForm({ ...form, default_unit: e.target.value })}
                       placeholder="metre, adet, TL..."
                       data-testid="category-unit-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
              <Button onClick={create} className="bg-emerald-700 hover:bg-emerald-800"
                      data-testid="category-submit-button">
                Ekle
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="category-grid">
        {cats.map((c) => (
          <Card key={c.id} className="p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`category-card-${c.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <div className="h-9 w-9 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center">
                  <Tags className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    Birim: <span className="font-mono">{c.default_unit}</span>
                  </div>
                </div>
              </div>
              {c.is_default ? (
                <Badge variant="secondary" className="text-[10px]">Varsayılan</Badge>
              ) : isAdmin ? (
                <Button variant="ghost" size="icon" onClick={() => remove(c)}
                        data-testid={`delete-category-${c.id}`}>
                  <Trash2 className="h-4 w-4 text-rose-600" />
                </Button>
              ) : null}
            </div>
          </Card>
        ))}
      </div>
    </AppLayout>
  );
}
